/**
 * Utility functions for formatting dates, currency, and other values.
 * Centralizes formatting logic for consistency across the application.
 */

/**
 * Ensure an ISO timestamp string is interpreted as UTC.
 * The backend stores naive UTC datetimes; some endpoints serialize them with a trailing "Z" while others (e.g. data_fetched_at, created_at) emit no timezone designator at all.
 * Per the ECMAScript spec, new Date("2026-06-04T03:30:00") with no designator is parsed as browser-LOCAL time, so an offset-less UTC value would be off by the browser's timezone (e.g. 8 hours on GMT+8).
 * If the string has a time component but no timezone marker, append "Z" so it is read as UTC.
 * Idempotent: strings that already carry "Z" or a numeric offset, and date-only strings, are returned unchanged.
 */
export const ensureUtc = (timestamp: string): string => {
  const hasTime = timestamp.includes("T");
  const hasTimezone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(timestamp);
  return hasTime && !hasTimezone ? `${timestamp}Z` : timestamp;
};

/**
 * Parse a backend-provided value into a Date, treating offset-less timestamp
 * strings as UTC. Use this instead of `new Date(value)` anywhere a server
 * timestamp is parsed for display or time math.
 */
export const parseServerDate = (date: string | number | Date): Date =>
  date instanceof Date
    ? date
    : new Date(typeof date === "string" ? ensureUtc(date) : date);

/**
 * Format a date string or Date object to a localized date string.
 *
 * @param date - ISO date string, timestamp, or Date object
 * @param options - Intl.DateTimeFormat options
 * @returns Formatted date string, or empty string if date is invalid
 */
export const formatDate = (
  date: string | number | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
  }
): string => {
  if (!date) return "";

  try {
    const dateObj = parseServerDate(date);

    // Check for invalid date
    if (isNaN(dateObj.getTime())) return "";

    return new Intl.DateTimeFormat("en-US", options).format(dateObj);
  } catch {
    return "";
  }
};

/**
 * Format a date to show only the date portion (e.g., "Feb 5, 2026")
 */
export const formatDateShort = (date: string | number | Date | null | undefined): string => {
  return formatDate(date, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

/**
 * Format a date to show date and time (e.g., "Feb 5, 2026, 3:30 PM")
 */
export const formatDateTime = (date: string | number | Date | null | undefined): string => {
  return formatDate(date, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

/**
 * Format a date for billing/subscription display (e.g., "February 5, 2026")
 */
export const formatBillingDate = (date: string | number | Date | null | undefined): string => {
  return formatDate(date, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

/**
 * Format a date to ISO date string (YYYY-MM-DD)
 */
export const formatISODate = (date: string | number | Date | null | undefined): string => {
  if (!date) return "";

  try {
    const dateObj = parseServerDate(date);
    if (isNaN(dateObj.getTime())) return "";
    return dateObj.toISOString().split("T")[0];
  } catch {
    return "";
  }
};

/**
 * Format a currency amount.
 * 
 * @param amount - Amount in dollars (not cents)
 * @param currency - ISO currency code (default: USD)
 * @param options - Additional Intl.NumberFormat options
 * @returns Formatted currency string
 */
export const formatCurrency = (
  amount: number,
  currency: string = "USD",
  options: Partial<Intl.NumberFormatOptions> = {}
): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    ...options,
  }).format(amount);
};

/**
 * Format a currency amount from cents to dollars.
 * 
 * @param cents - Amount in cents
 * @param currency - ISO currency code (default: USD)
 * @returns Formatted currency string
 */
export const formatCentsAsCurrency = (
  cents: number,
  currency: string = "USD"
): string => {
  return formatCurrency(cents / 100, currency);
};

/**
 * Format a percentage value.
 * 
 * @param value - Percentage value (e.g., 0.25 for 25% or 25 for 25%)
 * @param isDecimal - Whether the value is a decimal (0.25) or already a percentage (25)
 * @param decimals - Number of decimal places to show
 * @returns Formatted percentage string (e.g., "25%")
 */
export const formatPercentage = (
  value: number,
  isDecimal: boolean = false,
  decimals: number = 0
): string => {
  const percentage = isDecimal ? value * 100 : value;
  return `${percentage.toFixed(decimals)}%`;
};

/**
 * Calculate and format yearly savings percentage.
 * 
 * @param monthlyPrice - Monthly price
 * @param yearlyPrice - Yearly price
 * @returns Savings percentage (0 if no savings)
 */
export const calculateYearlySavings = (
  monthlyPrice: number,
  yearlyPrice: number
): number => {
  if (monthlyPrice <= 0 || yearlyPrice <= 0) return 0;
  
  const annualFromMonthly = monthlyPrice * 12;
  const savings = annualFromMonthly - yearlyPrice;
  const percentage = (savings / annualFromMonthly) * 100;
  
  // Round to 2 decimal places for precision, return 0 if no savings
  return percentage > 0 ? Math.round(percentage * 100) / 100 : 0;
};

/**
 * Format a number with thousands separators.
 */
export const formatNumber = (
  value: number,
  options: Partial<Intl.NumberFormatOptions> = {}
): string => {
  return new Intl.NumberFormat("en-US", options).format(value);
};

/**
 * Format an ISO timestamp as a human-friendly relative string for activity feeds.
 * Returns "Just now", "5m ago", "3h ago", "2d ago", or a short date for older entries.
 */
export const formatActivityDate = (iso: string | null | undefined): string => {
  if (!iso) return "Never";
  const d = parseServerDate(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: d.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
};

/**
 * Format an ISO timestamp as a full date-time string for tooltips / detail views.
 */
export const formatFullActivityDate = (iso: string | null): string => {
  if (!iso) return "—";
  const d = parseServerDate(iso);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};
